/**
 * masterGanttPrepMerge.ts
 * BUG-2026-0193 — fold a work order's rock-prep bookings into ONE "Preparation"
 * bar on the Master Gantt.
 *
 * Prep (Cutting / Grinding / Conformity) is inserted per test line, so a work
 * order with four rock tests carried 4 + 2 + 2 prep bookings and the Gantt
 * listed every one ("don't want to see all the individual cutting/grinding/
 * conformity bars, just one total bar shown as one task"). This module groups a
 * bucket's prep bookings into contiguous-working-day clusters (shared rule with
 * the Machine Schedule grid — utils/prepBookingMerge) and builds one leaf bar
 * per cluster:
 *   - Project view: ALL steps across ALL prep machines → one bar under the
 *     work order's Machines band ("Preparation · Cutting × 4, Grinding × 2 · …").
 *   - Machine view: that machine's prep for the work order → one bar
 *     ("RK-SAW — Preparation · Cutting × 4").
 *
 * The merged point keeps the `machineBooking` taskType and borrows the PRIMARY
 * member's `bookingKey` (earliest start, then key) for its id / `custom`, so the
 * conflict tagger, deep-links and the detail drawer keep resolving. Every
 * folded key rides along in `custom.bookingKeys` (multi-select + conflict
 * tagging), plus `custom.isPrepMerge` and the step / machine / minutes summary
 * for the tooltip and drawer. Single-member clusters fall back to the ordinary
 * buildBookingPoint. Pure — no DOM, no theme import.
 */

import type { MasterGanttMachineBooking } from '../../types/schedule.js';
import { resolvePointFill, type GanttFill, type GanttTaskType, type GanttStatus } from './ganttFillResolver.js';
import {
  buildBookingPoint,
  resolveOpacity,
  toMs,
  toEndMs,
  type GanttPoint,
  type GanttColorTokens,
} from './masterGanttRowBuilder.js';
import {
  clusterContiguous,
  prepStepBreakdown,
  prepTotalMinutes,
  formatMinutesCompact,
  weekdayOnly,
} from '../../utils/prepBookingMerge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One folded prep bar: every prep booking of a bucket within one contiguous run. */
export interface PrepBookingCluster {
  /** Earliest member (then lowest key) — its bookingKey becomes the bar id. */
  primary: MasterGanttMachineBooking;
  /** Every folded booking, earliest first (incl. primary). */
  members: MasterGanttMachineBooking[];
  bookingKeys: string[];
  /** Inclusive 'YYYY-MM-DD' span across the members. */
  startDate: string;
  endDate: string;
  /** Distinct machine codes, first-appearance order. */
  machineCodes: string[];
  machineKeys: string[];
  /** "Cutting × 4, Grinding × 2". */
  stepBreakdown: string;
  /** Σ member MachineMinutes, or null when none is known. */
  totalMinutes: number | null;
  /** 'Committed' only when every member is Committed; else 'Tentative'. */
  status: string;
  source: string;
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

/** True for a booking whose task is an auto-inserted prep step. */
export function isPrepGanttBooking(bk: MasterGanttMachineBooking): boolean {
  return bk.isPrepStep === true && !bk.eventType && !bk.isIndexAllocation && !!bk.scheduleTaskKey;
}

/** Split a bucket into prep bookings and everything else (order preserved). */
export function splitPrepBookings(
  bookings: readonly MasterGanttMachineBooking[],
): { prep: MasterGanttMachineBooking[]; rest: MasterGanttMachineBooking[] } {
  const prep: MasterGanttMachineBooking[] = [];
  const rest: MasterGanttMachineBooking[] = [];
  for (const bk of bookings) (isPrepGanttBooking(bk) ? prep : rest).push(bk);
  return { prep, rest };
}

function toCluster(members: MasterGanttMachineBooking[]): PrepBookingCluster {
  const sorted = [...members].sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || a.bookingKey.localeCompare(b.bookingKey),
  );
  const primary = sorted[0];
  let startDate = primary.startDate;
  let endDate = primary.endDate;
  const machineCodes: string[] = [];
  const machineKeys: string[] = [];
  let allCommitted = true;
  for (const m of sorted) {
    if (m.startDate < startDate) startDate = m.startDate;
    if (m.endDate > endDate) endDate = m.endDate;
    if (!machineKeys.includes(m.machineKey)) { machineKeys.push(m.machineKey); machineCodes.push(m.machineCode); }
    if (m.status !== 'Committed') allCommitted = false;
  }
  return {
    primary,
    members: sorted,
    bookingKeys: sorted.map((m) => m.bookingKey),
    startDate,
    endDate,
    machineCodes,
    machineKeys,
    stepBreakdown: prepStepBreakdown(sorted.map((m) => m.taskName)),
    totalMinutes: prepTotalMinutes(sorted.map((m) => m.machineMinutes)),
    status: allCommitted ? 'Committed' : 'Tentative',
    source: primary.source,
  };
}

/**
 * Cluster a bucket's prep bookings into contiguous-working-day runs (Mon–Fri
 * rule — the Gantt has no holiday feed; a stat-holiday gap merely splits a
 * cluster, never merges a wrong one). Ordered by start.
 */
export function clusterPrepBookings(prep: readonly MasterGanttMachineBooking[]): PrepBookingCluster[] {
  return clusterContiguous(prep, (b) => b.startDate, (b) => b.endDate, weekdayOnly).map(toCluster);
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * Bar label. `withMachine` (machine view) leads with the machine code like the
 * other bars on that row; the project view leads with "Preparation" because the
 * bar spans several machines.
 *   "RK-SAW — Preparation · Cutting × 4 · 1 h"
 *   "Preparation · Cutting × 4, Grinding × 2 · 2 h 20 min · RK-SAW, RK-GRIND"
 */
export function prepClusterLabel(cluster: PrepBookingCluster, withMachine: boolean): string {
  const parts = [`Preparation · ${cluster.stepBreakdown}`];
  if (cluster.totalMinutes != null) parts.push(formatMinutesCompact(cluster.totalMinutes));
  if (withMachine) return `${cluster.machineCodes[0]} — ${parts.join(' · ')}`;
  if (cluster.machineCodes.length > 0) parts.push(cluster.machineCodes.join(', '));
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Point builder
// ---------------------------------------------------------------------------

/**
 * One leaf bar for a prep cluster. A single-member cluster is the ordinary
 * booking bar (byte-identical id/custom to buildBookingPoint) so nothing
 * changes for a work order with one rock test.
 */
export function buildPrepClusterPoint(
  cluster: PrepBookingCluster,
  parentId: string,
  colors: GanttColorTokens,
  withMachine: boolean,
): GanttPoint {
  if (cluster.members.length === 1) return buildBookingPoint(cluster.primary, parentId, colors);

  const taskType: GanttTaskType = 'machineBooking';
  const status: GanttStatus = cluster.status === 'Committed' ? 'Committed' : 'Tentative';
  const fill: GanttFill = resolvePointFill(taskType, status, null, colors, 'attended');
  const bk = cluster.primary;

  return {
    id: `booking-${bk.bookingKey}`,
    parent: parentId,
    name: prepClusterLabel(cluster, withMachine),
    start: toMs(cluster.startDate),
    // BUG-2026-0095: inclusive end → exclusive end-of-day.
    end: toEndMs(cluster.endDate),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status,
    eventType: null,
    custom: {
      bookingKey: bk.bookingKey,
      bookingKeys: cluster.bookingKeys,
      machineKey: bk.machineKey,
      machineKeys: cluster.machineKeys,
      status: cluster.status,
      source: cluster.source,
      eventType: null,
      scheduleTaskKey: bk.scheduleTaskKey,
      runMode: 'attended',
      isIndexAllocation: false,
      scenarioKey: null,
      // BUG-2026-0193 — tooltip / drawer summary of the folded prep.
      isPrepMerge: true,
      prepStepCount: cluster.members.length,
      prepStepBreakdown: cluster.stepBreakdown,
      prepMachineCodes: cluster.machineCodes,
      prepTotalMinutes: cluster.totalMinutes,
    },
  };
}

/**
 * Convenience for the row builders: the prep bars for a bucket (one per
 * cluster, ordered by start) plus the non-prep remainder to lay out as before.
 */
export function buildPrepPointsForBucket(
  bookings: readonly MasterGanttMachineBooking[],
  parentId: string,
  colors: GanttColorTokens,
  withMachine: boolean,
): { prepPoints: GanttPoint[]; rest: MasterGanttMachineBooking[] } {
  const { prep, rest } = splitPrepBookings(bookings);
  const prepPoints = clusterPrepBookings(prep).map((c) => buildPrepClusterPoint(c, parentId, colors, withMachine));
  return { prepPoints, rest };
}
